import React, { useState } from "react";
import { api } from "../lib/api";
import { Home, Key, Handshake, ArrowRight, Check, Calculator as CalculatorIcon } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";

// Buyer/seller value pillars — mirrors the Recruit page structure so the
// public brand voice stays consistent (hero → 3 pillars → conversion form).
const PILLARS = [
  {
    id: "buyers",
    icon: Key,
    title: "For Buyers",
    points: [
      "Off-market and coming-soon Metro Atlanta listings",
      "Sharp pre-approval + inspection playbook",
      "Neighborhood + school-district guidance",
      "No pressure — we move at your pace",
    ],
  },
  {
    id: "sellers",
    icon: Home,
    title: "For Sellers",
    points: [
      "Professional photography + marketing kit",
      "MLS + social + email — priced to move",
      "Weekly reporting on views + showings",
      "Negotiation strategy tuned to your bottom line",
    ],
  },
  {
    id: "both",
    icon: Handshake,
    title: "Buying + Selling",
    points: [
      "Bridge timelines so you don't carry two mortgages",
      "Contingency and rent-back structuring",
      "One team, one point of contact",
      "Fewer surprises at the closing table",
    ],
  },
];

const INTENTS = [
  { id: "buy", label: "I'm looking to Buy" },
  { id: "sell", label: "I'm looking to Sell" },
  { id: "both", label: "Both — Buying + Selling" },
];

const TIMELINES = [
  { id: "asap", label: "ASAP" },
  { id: "1-3m", label: "1–3 months" },
  { id: "3-6m", label: "3–6 months" },
  { id: "6-12m", label: "6–12 months" },
  { id: "exploring", label: "Just exploring" },
];

export default function Clients() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    intent: "buy", timeline: "", neighborhood: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Browser-tab title only — OG / share-preview metadata lives in
  // public/index.html because crawlers don't execute JavaScript.
  React.useEffect(() => {
    const prev = document.title;
    document.title = "Let Us Help You Make The Move | CORE Realty and Associates";
    return () => { document.title = prev; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/public/client-lead", form);
      setSent(true);
      toast.success("We got it — a CORE agent will reach out shortly");
    } catch {
      toast.error("Send failed — please try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="clients-page">
      <header className="border-b border-[#d1cece] px-8 py-5 flex items-center justify-between">
        <Logo variant="black" height={44} />
        <a href="/login" className="text-sm hover:text-[#bf858e]" data-testid="clients-agent-login">Agent Login →</a>
      </header>

      <section className="relative px-8 lg:px-16 py-24 lg:py-32 overflow-hidden bg-[#050303]" data-testid="clients-hero">
        {/* Bright midday Atlanta skyline (Bank of America Plaza + downtown +
            lush trees). 4K 24fps @ ~12 MB — feels premium and welcoming.
            Poster identical for instant paint. Video plays at 100 %
            brightness; a directional bottom-left gradient shields the copy
            block only, so the skyline itself stays vibrant. */}
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay muted loop playsInline preload="metadata"
          poster="https://images.pexels.com/videos/33134748/aerial-atlanta-downtown-atlanta-drone-33134748.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
          data-testid="clients-hero-video"
        >
          <source src="https://videos.pexels.com/video-files/33134748/14122289_2560_1440_24fps.mp4" type="video/mp4" />
          <source src="https://videos.pexels.com/video-files/2818522/2818522-hd_1920_1080_24fps.mp4" type="video/mp4" />
        </video>
        {/* Directional gradient — dark under the text block, transparent
            toward the skyline. Bottom band adds tiny extra falloff for
            headline shadow depth. */}
        <div className="absolute inset-0 bg-gradient-to-tr from-[#050303]/85 via-[#050303]/45 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050303]/30" />
        <div className="relative max-w-4xl text-white" style={{ textShadow: "0 2px 24px rgba(0,0,0,0.55)" }}>
          <div className="mb-3 text-[11px] lg:text-xs font-bold tracking-[0.25em] uppercase text-[#e1b7c1]" data-testid="clients-eyebrow">
            Real Estate Integrity At Its CORE
          </div>
          <div className="overline mb-4 text-white/80 font-semibold">Buyers & Sellers · Metro Atlanta</div>
          <h1 className="font-heading font-bold text-5xl lg:text-7xl leading-[1.05] tracking-tight text-white">
            Let Us Help You Make The Move
          </h1>
          <p className="mt-6 text-lg lg:text-xl font-medium text-white max-w-2xl">
            CORE Realty and Associates has helped people buy, sell, and invest in homes all over the Metro Atlanta area. We are experienced and knowledgeable about the current market trends and neighborhoods. Experience real estate with a boutique, personalized touch.
          </p>
          <a href="#connect" className="mt-10 inline-flex items-center gap-2 bg-[#e1b7c1] text-[#050303] font-bold pill-btn hover:bg-white shadow-lg" data-testid="clients-cta" style={{ textShadow: "none" }}>
            Start your journey <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* Calculator CTA — easy-to-spot bridge between hero and pillars so
          window-shoppers can run the numbers before filling out the form. */}
      <section
        className="px-8 lg:px-16 py-10 bg-gradient-to-r from-[#e1b7c1]/40 via-white to-[#e1b7c1]/40 border-y border-[#d1cece]"
        data-testid="clients-calc-cta"
      >
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-start md:items-center gap-6 md:justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#bf858e] text-white flex items-center justify-center shrink-0">
              <CalculatorIcon size={22} strokeWidth={1.5} />
            </div>
            <div>
              <div className="overline text-[#bf858e]">Free · No sign-up</div>
              <h3 className="font-heading text-2xl lg:text-3xl mt-1 leading-tight">How much home can you afford?</h3>
              <p className="text-sm text-[#050303]/70 mt-1 max-w-xl">
                Five quick steps, estimates that update as you type — then we help you turn the plan into a move.
              </p>
            </div>
          </div>
          <a
            href="/calculator"
            className="inline-flex items-center gap-2 bg-[#050303] text-white font-bold pill-btn hover:bg-[#bf858e] shadow-lg whitespace-nowrap"
            data-testid="clients-calc-cta-btn"
          >
            Use Our Free Mortgage Calculator <ArrowRight size={16} />
          </a>
        </div>
      </section>


      <section className="px-8 lg:px-16 py-20 max-w-7xl" data-testid="clients-pillars">
        <div className="overline">Why CORE Realty and Associates</div>
        <h2 className="font-heading text-4xl lg:text-5xl mt-2 mb-3 max-w-3xl">
          Local. Independent. Honest at every step.
        </h2>
        <p className="text-sm text-[#050303]/70 mb-12 max-w-2xl">
          Whether you&apos;re house-hunting for the first time or listing a legacy home, here&apos;s what working with us looks like.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.id}
                className={`core-card p-7 ${i === 1 ? "bg-gradient-to-br from-white to-[#e1b7c1]/30" : ""}`}
                data-testid={`clients-pillar-${p.id}`}
              >
                <div className="w-11 h-11 rounded-full bg-[#e1b7c1]/40 text-[#bf858e] flex items-center justify-center">
                  <Icon size={18} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-2xl mt-5">{p.title}</h3>
                <ul className="mt-5 space-y-2">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2 text-sm">
                      <Check size={14} strokeWidth={2} className="mt-0.5 text-[#bf858e] shrink-0" /> {pt}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      <section id="connect" className="px-8 lg:px-16 py-20 bg-[#050303] text-white">
        <Logo variant="black" height={56} className="mb-10 bg-white p-3 rounded-lg inline-block" />
        <div className="max-w-2xl">
          <div className="overline text-white/70">Let&apos;s connect</div>
          <h2 className="font-heading text-5xl mt-2 mb-8">Tell us about your move.</h2>
          {sent ? (
            <div className="core-card p-7 bg-white text-[#050303]" data-testid="clients-success">
              <h3 className="font-heading text-2xl">Thank you.</h3>
              <p className="text-sm mt-2 text-[#050303]/70">
                We&apos;ve emailed a confirmation and a CORE Realty and Associates agent will personally reach out within one business day. Check your inbox for our welcome note.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="clients-form">
              <input
                required placeholder="Full name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#e1b7c1]"
                data-testid="clients-name"
              />
              <input
                required type="email" placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#e1b7c1]"
                data-testid="clients-email"
              />
              <input
                required placeholder="Phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#e1b7c1]"
                data-testid="clients-phone"
              />

              <div>
                <div className="text-xs text-white/60 mb-2 mt-4 uppercase tracking-widest">I&apos;m looking to&hellip;</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {INTENTS.map((it) => (
                    <button
                      key={it.id} type="button"
                      onClick={() => setForm({ ...form, intent: it.id })}
                      className={`px-4 py-3 rounded-lg border text-sm transition text-left ${
                        form.intent === it.id
                          ? "border-[#e1b7c1] bg-[#e1b7c1]/20 text-white"
                          : "border-white/20 bg-white/5 text-white/70 hover:border-[#e1b7c1]"
                      }`}
                      data-testid={`clients-intent-${it.id}`}
                    >
                      {it.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <div>
                  <div className="text-xs text-white/60 mb-2 uppercase tracking-widest">Timeline (optional)</div>
                  <select
                    value={form.timeline}
                    onChange={(e) => setForm({ ...form, timeline: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white focus:outline-none focus:border-[#e1b7c1]"
                    data-testid="clients-timeline"
                  >
                    <option value="" className="bg-[#050303]">Select&hellip;</option>
                    {TIMELINES.map((t) => (
                      <option key={t.id} value={t.id} className="bg-[#050303]">{t.label}</option>
                    ))}
                  </select>
                </div>
                <input
                  placeholder="Neighborhood or area (optional)"
                  value={form.neighborhood}
                  onChange={(e) => setForm({ ...form, neighborhood: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#e1b7c1] mt-6 sm:mt-6"
                  data-testid="clients-neighborhood"
                />
              </div>

              <textarea
                placeholder="Tell us a bit about what you're looking for (optional)"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#e1b7c1]"
                data-testid="clients-message"
              />

              <button
                type="submit" disabled={sending}
                className="bg-[#e1b7c1] text-[#050303] pill-btn hover:bg-white disabled:opacity-60"
                data-testid="clients-submit"
              >
                {sending ? "Sending…" : "Send to CORE Realty and Associates"}
              </button>
              <p className="text-[11px] text-white/50 pt-1">
                We&apos;ll email you a confirmation and reach out within one business day. Your details are never sold or shared.
              </p>
            </form>
          )}
        </div>
      </section>

      <footer className="px-8 py-8 border-t border-[#d1cece]" data-testid="clients-footer">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Logo variant="black" height={32} />
            <span
              className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#bf858e]"
              data-testid="brand-motto"
            >
              Real Estate Integrity at its CORE
            </span>
          </div>
          <div className="text-xs text-[#050303]/50">
            © {new Date().getFullYear()} CORE Realty and Associates LLC · Snellville, GA
          </div>
        </div>
      </footer>
    </div>
  );
}
